class ScrollToFull extends HTMLElement {
  constructor() {
    super();
    this.sectionTop = 0;
    this.sectionHeight = 0;
    this.viewportWidth = 0;
    this.viewportHeight = 0;
    this.startWidth = 0;
    this.startHeight = 0;
    this.initialRadius = 0;
    this.topOffset = 0;
    this.ticking = false;
    this.textVisible = false;
    this.detailOnly = false;
    this.detailSettleTimer = null;
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.update = this.update.bind(this);
  }

  connectedCallback() {
    this.imageBox = this.querySelector('.stf-image-box');
    this.image = this.querySelector('.stf-image');
    this.textWrapper = this.querySelector('.stf-image-text-wrapper');
    this.subtitle = this.querySelector('.stf-text-subtitle');
    this.title = this.querySelector('.stf-text-title');
    this.paragraph = this.querySelector('.stf-text-paragraph');

    if (!this.imageBox) return;

    this.mobileBreakpoint = this.readPx(this.dataset.mobileBreakpoint, 768);
    this.classList.add('is-text-hidden');
    this.syncScreen();
    this.syncTextAlignment();
    this.syncTextPosition();
    this.measure();
    this.update();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    if (this.image && !this.image.complete) {
      this.image.addEventListener('load', this.onResize, { once: true });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.measureText();
        this.requestTick();
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.detailSettleTimer) window.clearTimeout(this.detailSettleTimer);
  }

  onScroll() {
    this.requestTick();
  }

  onResize() {
    this.syncScreen();
    this.measure();
    this.syncTextAlignment();
    this.syncTextPosition();
    this.requestTick();
  }

  requestTick() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  }

  measure() {
    const rect = this.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const isMobile = this.isMobile();

    this.viewportWidth = window.innerWidth;
    this.viewportHeight = window.innerHeight;
    this.sectionTop = rect.top + scrollY;

    const styles = window.getComputedStyle(this);
    const initialWidth = this.readPx(styles.getPropertyValue('--stf-initial-width'), 1200);
    const initialHeight = this.readPx(styles.getPropertyValue('--stf-initial-height'), 600);
    this.initialRadius = this.readPx(styles.getPropertyValue('--stf-initial-radius'), 0);
    const topOffsetVar = isMobile
      ? styles.getPropertyValue('--stf-top-offset-mobile') || styles.getPropertyValue('--stf-top-offset')
      : styles.getPropertyValue('--stf-top-offset');
    this.topOffset = Math.max(0, this.readPx(topOffsetVar, 0));
    this.startWidth = Math.min(initialWidth, this.viewportWidth - 40);
    this.startHeight = isMobile
      ? this.startWidth * (this.getImageRatio(initialWidth, initialHeight) || (initialHeight / initialWidth))
      : this.startWidth * (initialHeight / initialWidth);

    this.style.setProperty('--stf-start-width', `${this.startWidth}px`);
    this.style.setProperty('--stf-start-height', `${this.startHeight}px`);
    if (isMobile) {
      this.style.setProperty(
        '--stf-mobile-section-height',
        `${Math.round(this.startHeight + this.viewportHeight)}px`,
      );
    } else {
      this.style.removeProperty('--stf-mobile-section-height');
    }

    this.sectionHeight = this.offsetHeight;
    this.measureText();
  }

  measureText() {
    if (!this.textWrapper) return;

    const isMobile = this.isMobile();
    const styles = window.getComputedStyle(this);
    const paragraphMargin = this.readPx(styles.getPropertyValue(
      isMobile ? '--stf-paragraph-margin-top-mobile' : '--stf-paragraph-margin-top',
    ), 0);
    const paragraphHeight = this.paragraph
      ? Math.ceil(this.paragraph.getBoundingClientRect().height || this.paragraph.scrollHeight || 0)
      : 0;
    const introHeight = Math.ceil((this.subtitle?.offsetHeight || 0) + (this.title?.offsetHeight || 0));

    this.style.setProperty('--stf-intro-text-height', `${introHeight}px`);
    const detailOffset = isMobile ? paragraphMargin * 2 : paragraphMargin;

    this.style.setProperty('--stf-detail-text-height', `${detailOffset + paragraphHeight}px`);
    this.style.setProperty(
      '--stf-active-text-height',
      `${this.detailOnly ? detailOffset + paragraphHeight : introHeight}px`,
    );
  }

  update() {
    this.ticking = false;

    const scrollY = window.scrollY || window.pageYOffset;
    const topOffset = this.topOffset || 0;
    const growEnd = this.sectionTop - topOffset;
    const growStart = growEnd - this.viewportHeight * 0.8;
    const growProgress = this.clamp((scrollY - growStart) / Math.max(growEnd - growStart, 1), 0, 1);
    const easedGrow = this.easeOutCubic(growProgress);

    const isMobile = this.isMobile();
    const targetWidth = this.viewportWidth;
    const targetScaleX = targetWidth / this.startWidth;
    const effectiveHeight = Math.max(0, this.viewportHeight - (this.topOffset || 0));
    const targetScaleY = isMobile
      ? targetScaleX
      : effectiveHeight / this.startHeight;
    const scaleX = this.lerp(1, targetScaleX, easedGrow);
    const scaleY = this.lerp(1, targetScaleY, easedGrow);
    const radius = this.lerp(this.initialRadius, 0, easedGrow);

    this.style.setProperty('--stf-scale-x', scaleX.toFixed(5));
    this.style.setProperty('--stf-scale-y', scaleY.toFixed(5));
    const maxScale = Math.max(scaleX, scaleY);
    this.style.setProperty('--stf-img-scale-x', (maxScale / scaleX).toFixed(5));
    this.style.setProperty('--stf-img-scale-y', (maxScale / scaleY).toFixed(5));
    this.style.setProperty('--stf-radius-current', `${radius.toFixed(2)}px`);
    this.style.setProperty('--stf-content-opacity', (1 - easedGrow).toFixed(3));

    if (isMobile) {
      const textShouldShow = scrollY >= this.sectionTop - this.viewportHeight * 0.9 &&
        scrollY <= this.sectionTop + this.sectionHeight;

      this.classList.toggle('is-mobile-text-active', textShouldShow);
      this.setTextVisible(false);
      this.setDetailOnly(false);
      return;
    }

    const textStart = isMobile
      ? this.sectionTop - this.viewportHeight
      : this.sectionTop + this.viewportHeight * 0.08;
    const textShouldShow = scrollY >= textStart && scrollY <= this.sectionTop + this.sectionHeight;
    const detailOnlyStart = isMobile
      ? this.sectionTop + this.sectionHeight * 0.5
      : this.sectionTop + this.viewportHeight * 0.65;
    const detailOnlyShouldShow = textShouldShow && scrollY >= detailOnlyStart;
    this.setTextVisible(textShouldShow);
    this.setDetailOnly(detailOnlyShouldShow);
  }

  setTextVisible(visible) {
    if (this.textVisible === visible) return;
    this.textVisible = visible;
    this.classList.toggle('is-text-visible', visible);
    this.classList.toggle('is-text-hidden', !visible);
    if (!visible) this.setDetailOnly(false);
  }

  setDetailOnly(visible) {
    if (this.detailOnly === visible) return;
    this.detailOnly = visible;
    this.style.setProperty(
      '--stf-active-text-height',
      visible ? 'var(--stf-detail-text-height)' : 'var(--stf-intro-text-height)',
    );
    this.classList.toggle('is-detail-only', visible);
    this.classList.toggle('is-detail-transitioning', visible);
    this.classList.remove('is-detail-settled');

    if (this.detailSettleTimer) window.clearTimeout(this.detailSettleTimer);
    if (visible) {
      this.detailSettleTimer = window.setTimeout(() => {
        if (!this.detailOnly) return;
        this.classList.remove('is-detail-transitioning');
        this.classList.add('is-detail-settled');
      }, 780);
    } else {
      this.classList.remove('is-detail-transitioning');
    }
  }

  syncTextPosition() {
    const isMobile = this.isMobile();
    const position = isMobile
      ? this.dataset.imageTextPositionMobile || this.dataset.imageTextPosition || 'top'
      : this.dataset.imageTextPosition || 'bottom';

    this.dataset.textPosition = position;
  }

  syncTextAlignment() {
    const isMobile = this.isMobile();
    const subtitleAlign = isMobile
      ? this.dataset.subtitleAlignMobile || this.dataset.subtitleAlign || 'center'
      : this.dataset.subtitleAlign || 'center';
    const titleAlign = isMobile
      ? this.dataset.titleAlignMobile || this.dataset.titleAlign || 'center'
      : this.dataset.titleAlign || 'center';
    const paragraphAlign = isMobile
      ? this.dataset.paragraphAlignMobile || this.dataset.paragraphAlign || 'center'
      : this.dataset.paragraphAlign || 'center';

    this.style.setProperty('--stf-subtitle-align', subtitleAlign);
    this.style.setProperty('--stf-title-align', titleAlign);
    this.style.setProperty('--stf-paragraph-align', paragraphAlign);
    this.style.setProperty('--stf-subtitle-align-mobile', subtitleAlign);
    this.style.setProperty('--stf-title-align-mobile', titleAlign);
    this.style.setProperty('--stf-paragraph-align-mobile', paragraphAlign);
  }

  readPx(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  getScrollY() {
    return window.scrollY || window.pageYOffset || 0;
  }

  getImageRatio(initialWidth, initialHeight) {
    if (this.image) {
      const naturalWidth = this.image.naturalWidth;
      const naturalHeight = this.image.naturalHeight;

      if (naturalWidth > 0 && naturalHeight > 0) {
        return naturalHeight / naturalWidth;
      }
    }

    return initialHeight / initialWidth;
  }

  isMobile() {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  syncScreen() {
    this.dataset.stfScreen = this.isMobile() ? 'mobile' : 'desktop';
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  easeOutCubic(value) {
    return 1 - Math.pow(1 - value, 3);
  }
}

if (!customElements.get('scroll-to-full')) {
  customElements.define('scroll-to-full', ScrollToFull);
}
